import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Calendar, Clock, Users, Utensils } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
export default function ReserveYourTable() {
  return <section data-section-id="9775" id="begin-your-journey" className="bg-background text-foreground relative py-24 px-4 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="w-full h-full bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614369/img/lace_detail_bw.png)] bg-cover bg-center bg-no-repeat grayscale" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto">
        <div className="border border-primary p-8 md:p-12 bg-background/80 backdrop-blur-sm shadow-2xl rounded-none">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-serif text-primary mb-4 tracking-tight">
              The First Step to Empowerment
            </h2>
            <p className="text-muted-foreground font-sans text-sm tracking-widest uppercase">
              Inquire about your bespoke luxury session
            </p>
          </div>

          <FormProvider formKey="journey_form_home" formId="journey_form_home" sectionName="reserve-your-table" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          reservation_date: "",
          reservation_time: "",
          guest_count: "",
          special_requests: ""
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          reservation_date: {
            required: true
          },
          reservation_time: {
            required: true
          },
          guest_count: {
            required: true,
            min: 1,
            max: 20
          },
          special_requests: {
            required: false
          }
        }} submitText="Submit Inquiry" successMessage="Your message has been sent. We will be in touch shortly to begin your journey." errorMessage="We could not complete your inquiry. Please try again or email us directly.">
            <ReservationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12 space-y-6 animate-in fade-in duration-500">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-none border border-primary text-primary mb-4">
          <Utensils className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif text-primary">
          Inquiry Received
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none uppercase tracking-widest">
          Send Another Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "border-0 border-b border-primary rounded-none bg-transparent px-0 py-2 h-auto focus-visible:ring-0 focus-visible:border-black shadow-none placeholder:text-muted-foreground/40 transition-colors text-foreground font-sans";
  const labelClasses = "text-primary uppercase tracking-widest text-xs font-medium mb-2 font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="555-0123" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="guest_count" rules={{
        required: "Number of subjects is required",
        min: {
          value: 1,
          message: "Minimum 1 subject"
        },
        max: {
          value: 20,
          message: "Maximum 20 subjects"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Number of Subjects</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="number" placeholder="1" min={1} max={20} {...field} className={`${inputClasses} pl-8`} />
                  <Users className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-primary opacity-50" />
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="reservation_date" rules={{
        required: "Date is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Preferred Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className={`${inputClasses} pl-8 [&::-webkit-calendar-picker-indicator]:invert-0`} />
                  <SafeHydrate><Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-primary opacity-50" /></SafeHydrate>
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />

        {}
        <FormField control={control} name="reservation_time" rules={{
        required: "Time is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Preferred Start Time</FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className={`${inputClasses} w-full appearance-none pl-8 cursor-pointer`}>
                    <option value="" disabled>
                      Select a time
                    </option>
                    {["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM"].map(time => <option key={time} value={time} className="bg-background text-foreground">
                        {time}
                      </option>)}
                  </select>
                  <Clock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-primary opacity-50" />
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>Tell us your vision</FormLabel>
            <FormControl>
              <textarea placeholder="Any specific details or questions you'd like to share..." {...field} className={`${inputClasses} w-full min-h-[80px] resize-none`} />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-zinc-800 transition-all duration-300 uppercase tracking-[0.2em] font-bold py-6 h-auto rounded-none text-sm md:text-base shadow-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Sending...
          </> : "Submit Inquiry"}
      </Button>
    </Form>;
}